import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  LinearProgress,
} from '@mui/material';
import { SkillNode } from '../types/microsoftGraph';
// import { index } from 'd3';

interface SkillsScoreTableProps {
  skillNodes: SkillNode[];
  showProgress?: boolean;
  competencyScores?: Map<number, { score: number; attempts: number }>; // ✅ NEW: Accept competency data
}

export const SkillsScoreTable: React.FC<SkillsScoreTableProps> = ({ 
  skillNodes,
  showProgress = true,
  competencyScores // ✅ NEW: Destructure competency data
}) => {
  const [sortedNodes, setSortedNodes] = useState<SkillNode[]>([]);

  useEffect(() => {
    if (!skillNodes || skillNodes.length === 0) {
      setSortedNodes([]);
      return;
    }

    // ✅ FIX: Deduplicate by SkillID first
    const uniqueNodesMap = new Map<number, SkillNode>();
    
    skillNodes.forEach(node => {
      const existing = uniqueNodesMap.get(node.SkillID);
      const competency = competencyScores?.get(node.SkillID);
      
      // Keep the node with the highest KeyNum (most recent positioning)
      if (!existing || (node.KeyNum && node.KeyNum > (existing.KeyNum || 0))) {
        uniqueNodesMap.set(node.SkillID, node);
      }
    });

    const uniqueNodes = Array.from(uniqueNodesMap.values());
    
    // ✅ FIX: Sort by the KeyNum that was already assigned by canvas
    const sorted = uniqueNodes.sort((a, b) => {
      const aKey = a.KeyNum || 0;
      const bKey = b.KeyNum || 0;
      return aKey - bKey;
    });

    // ✅ FIX: Use the existing KeyNum from nodes (don't recalculate!)
    const withSequence = sorted.map((node) => {
      const competency = competencyScores?.get(node.SkillID);
      
      return {
        ...node,
        KeyNum: node.KeyNum || 0, // ✅ Use existing KeyNum from canvas
        AverageSkillScore: competency?.score ?? node.AverageSkillScore ?? 0,
        SkillAttempts: competency?.attempts ?? (node as any).SkillAttempts ?? 0,
      };
    });

    console.log('[SkillsScoreTable] Displaying', withSequence.length, 'unique skills with KeyNums:', 
      withSequence.map(n => `${n.SkillName}: ${n.KeyNum}`));
    setSortedNodes(withSequence);
  }, [skillNodes, competencyScores]);

  // Helper function to get color based on score
  const getScoreColor = (score: number): string => {
    if (score === 0) return '#808080'; // Grey for no data
    if (score >= 4.5) return '#4CAF50'; // Green - Mastered
    if (score >= 3.5) return '#7FC049'; // Light green - Proficient
    if (score >= 2.5) return '#C9D144'; // Yellow-green - Developing
    if (score >= 1.5) return '#E29A3E'; // Orange - Emerging
    return '#F44336'; // Red - Beginning
  };

  // Get proficiency level label
  const getProficiencyLabel = (score: number): string => {
    if (score === 0) return 'Not Assessed';
    if (score >= 4.5) return 'Mastered';
    if (score >= 3.5) return 'Proficient';
    if (score >= 2.5) return 'Developing';
    if (score >= 1.5) return 'Emerging';
    return 'Beginning';
  };

  // Calculate overall progress
  const calculateProgress = () => {
    const totalSkills = sortedNodes.length;
    const assessedSkills = sortedNodes.filter(node => 
      (node.AverageSkillScore || 0) > 0
    ).length;
    return totalSkills > 0 ? (assessedSkills / totalSkills) * 100 : 0;
  };

  if (sortedNodes.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No skills to display
        </Typography>
      </Paper>
    );
  }

  const progress = calculateProgress();

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          My Skills Progress ({sortedNodes.length} skills)
        </Typography>
        
        {showProgress && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Overall Progress
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                {progress.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: progress >= 80 ? '#4CAF50' : 
                                   progress >= 50 ? '#FFC107' : '#FF9800'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {sortedNodes.filter(n => (n.AverageSkillScore || 0) > 0).length} of {sortedNodes.length} assessed
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
            <TableCell 
              align="center" 
              sx={{ 
                fontWeight: 'bold',
                minWidth: 50,
                backgroundColor: '#e0e0e0'
              }}
            >
              #
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 'bold',
                minWidth: 200,
                backgroundColor: '#e0e0e0'
              }}
            >
              Skill Name
            </TableCell>
            <TableCell 
              align="center"
              sx={{ 
                fontWeight: 'bold',
                minWidth: 100,
                backgroundColor: '#e0e0e0'
              }}
            >
              Score
            </TableCell>
            <TableCell 
              align="center"
              sx={{ 
                fontWeight: 'bold',
                minWidth: 120,
                backgroundColor: '#e0e0e0'
              }}
            >
              Proficiency
            </TableCell>
            <TableCell 
              align="center"
              sx={{ 
                fontWeight: 'bold',
                minWidth: 80,
                backgroundColor: '#e0e0e0'
              }}
            >
              Attempts
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedNodes.map((node, index) => {
            const score = node.AverageSkillScore || 0;
            const attempts = node.SkillAttempts || 0;
            const scoreColor = getScoreColor(score);
            const proficiency = getProficiencyLabel(score);
            
            return (
              <TableRow 
                key={`skill-${node.SkillID}`}
                hover
                sx={{
                  '&:hover': {
                    backgroundColor: '#f0f7ff',
                    cursor: 'pointer'
                  }
                }}
              >
                {/* Key Number */}
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    color: '#1976d2'
                  }}
                >
                  {node.KeyNum}
                </TableCell>
                
                {/* Skill Name */}
                <TableCell sx={{ fontWeight: 500 }}>
                  {node.SkillName}
                </TableCell>
                
                {/* Score Chip */}
                <TableCell align="center">
                  <Chip
                    label={score > 0 ? score.toFixed(1) : 'N/A'}
                    size="small"
                    sx={{
                      backgroundColor: scoreColor,
                      color: '#fff',
                      fontWeight: 'bold',
                      minWidth: 60,
                      fontSize: '0.875rem'
                    }}
                  />
                </TableCell>
                
                {/* Proficiency Level */}
                <TableCell align="center">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: score > 0 ? scoreColor : '#999'
                    }}
                  >
                    {proficiency}
                  </Typography>
                </TableCell>
                
                {/* Attempts Count */}
                <TableCell align="center">
                  <Chip
                    label={attempts}
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 50,
                      fontWeight: 'bold',
                      borderColor: attempts > 0 ? '#1976d2' : '#ccc',
                      color: attempts > 0 ? '#1976d2' : '#999'
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Summary Footer */}
      <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderTop: '1px solid #ddd' }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Score Key:</strong> 5.0 = Mastered | 4.0 = Proficient | 3.0 = Developing | 2.0 = Emerging | 1.0 = Beginning
        </Typography>
      </Box>
    </TableContainer>
  );
};

export default SkillsScoreTable;